import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useParams } from 'react-router-dom';
import { Table, Button, notification, DatePicker, Input } from 'antd';
import { DownloadOutlined, LineChartOutlined, SearchOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
import { AuthContext } from 'context/AuthContext';
import reportService from 'services/report.service';
import { formatDateForClient, formatDateForServer, formatMoney } from 'helpers/utils';
import { storage } from 'helpers';
import download from 'js-file-download';
import reportTypes from '../../../helpers/reportTypes';

const { RangePicker } = DatePicker;

const renderFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
  <div style={{ padding: 8 }}>
    <Input
      placeholder="Gün Sonu ID Ara"
      value={selectedKeys[0]}
      onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      onPressEnter={() => this.handleSearch(selectedKeys, confirm, 'Eod')}
      style={{ width: 188, marginBottom: 8, display: 'block' }}
    />
    <Button
      type="primary"
      onClick={() => this.handleSearch(selectedKeys, confirm, 'Eod')}
      icon={<SearchOutlined />}
      size="small"
      style={{ width: 90, marginRight: 8 }}
    >
      Ara
    </Button>
    <Button onClick={() => this.handleReset(clearFilters)} size="small" style={{ width: 90 }}>
      Temizle
    </Button>
  </div>
);

class EodReportByBranch extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      reports: [],
      loading: false,
      startDate: storage.get('date-filters')
        ? storage.get('date-filters').startDate
        : formatDateForClient(dayjs().subtract(365, 'days')),
      endDate: storage.get('date-filters')
        ? storage.get('date-filters').endDate
        : formatDateForClient(),
      currentMerch: context?.selectedMerch,
      searchText: '',
      searchedColumn: '',
      columns: [
        {
          title: <div className="table-title-calibri-bold-12">Şube Bilgisi</div>,
          dataIndex: 'BranchId',
          key: 'BranchId',
          align: 'center',
          render: (text, row) => {
            return (
              <span className="table-title-calibri-bold-12">
                {row.BranchName ? row.BranchName : ''} - {row.BranchId ? row.BranchId : ''}
              </span>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">Terminal Bilgisi</div>,
          dataIndex: 'SicilNumber',
          key: 'SicilNumber',
          align: 'center',
          render: (text, row) => {
            return (
              <div style={{ textAlign: 'center' }}>
                <span className="table-content-calibri-bold-11">
                  {' '}
                  {'Sicil No: '}
                  {row.TerminalSicilNumber ? row.TerminalSicilNumber : ''}{' '}
                </span>
                <br />
                <span className="table-content-calibri-light-11">
                  {' '}
                  {'  Terminal No: '}
                  {row.TerminalNumber ? row.TerminalNumber : ''}
                </span>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">Gün Sonu Tutarı</div>,
          dataIndex: 'Amount',
          key: 'Amount',
          align: 'center',
          width: '160px',
          render: (text, row) => {
            return (
              <div className="text-align-center">
                <span className="table-title-calibri-bold-12">{`${formatMoney(row.TotalEodAmount)} TL`}</span>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">Gün Sonu ID</div>,
          dataIndex: 'EodId',
          key: 'EodId',
          align: 'center',
          onFilter: (value, record) => record.EodId.toString().indexOf(value) === 0,
          filterDropdown: renderFilterDropdown,
          render: (text, row) => {
            return (
              <div style={{ textAlign: 'center' }}>
                <span className="table-title-calibri-bold-12">{row.EodId} </span>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">Gün Sonu Tarihi</div>,
          dataIndex: 'EodDate',
          key: 'EodDate',
          align: 'center',
          width: '160px',
          render: (text, row) => {
            return (
              <div className="text-align-center">
                <span className="table-title-calibri-bold-12">
                  {dayjs(row.EodDate).format('DD.MM.YYYY HH:mm')}{' '}
                </span>
                <br />
              </div>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">İşlemler</div>,
          dataIndex: 'Actions',
          key: 'Actions',
          align: 'center',
          fixed: 'right',
          width: 250,
          render: (text, row) => (
            <div>
              <NavLink
                target="_blank"
                to={{
                  pathname: `/raporlar/islem-bazli-gun-sonu-raporu/${row.BranchId}/${row.TerminalNumber}`,
                }}
              >
                <Button className="mr-3" type="primary">
                  <LineChartOutlined />
                  <span>İşlem Raporları</span>
                </Button>
              </NavLink>
            </div>
          ),
        },
      ],
    };
  }

  componentDidMount() {
    this.getReports();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState?.currentMerch?.number !== this.context?.selectedMerch?.number &&
      this.state?.currentMerch?.number === prevState.currentMerch?.number
    ) {
      this.getReports();
    }
  }

  getReports = async () => {
    this.setState({ loading: true, currentMerch: this.context?.selectedMerch }, async () => {
      try {
        const { currentMerch, endDate, startDate } = this.state;
        if (currentMerch?.number) {
          const { branchId, terminalId } = this.props.params;
          const { payload } = await reportService.getEodReportsByTerminal(
            currentMerch?.number,
            branchId,
            terminalId,
            formatDateForServer(startDate),
            formatDateForServer(endDate),
          );
          this.setState({ loading: false, reports: payload });
        }
      } catch (error) {
        this.setState({ loading: false, reports: [] });
        notification.error({
          message: 'İşlem Başarısız',
          description: error?.message || String(error),
        });
      }
    });
  };

  onDateChange = (dates) => {
    if (!dates || dates.length !== 2) return;
    const startDate = formatDateForClient(dates[0]);
    const endDate = formatDateForClient(dates[1]);
    this.setState({ startDate, endDate }, () => {
      storage.set({ startDate, endDate }, 'date-filters');
      this.getReports();
    });
  };

  downloadExcel = async () => {
    try {
      this.setState({ downloading: true });
      const { currentMerch, startDate, endDate } = this.state;
      const { branchId, terminalId } = this.props.params;
      const data = await reportService.getEodReportsExcel(
        currentMerch?.number,
        reportTypes.EOD_BY_CLIENT,
        branchId,
        terminalId,
        formatDateForServer(startDate),
        formatDateForServer(endDate),
      );
      download(
        data,
        `TerminalBazliGunSonuRaporu-${currentMerch?.number}-${branchId}-${dayjs().format('DD-MM-YYYY')}.xls`,
      );
      this.setState({ downloading: false });
    } catch (error) {
      this.setState({ loading: false, downloading: false });
      notification.error({
        message: 'İşlem Başarısız',
        description: error?.message || String(error),
      });
    }
  };

  render() {
    const { reports, columns, loading, currentMerch, startDate, endDate, downloading } = this.state;

    return (
      <>
        <PageContainer
          style={{ paddingBottom: 0 }}
          title="Terminal Bazında Günsonu Raporu"
          extra={[
            <RangePicker
              allowClear={false}
              value={[dayjs(startDate, 'DD.MM.YYYY'), dayjs(endDate, 'DD.MM.YYYY')]}
              key="eod"
              onChange={this.onDateChange}
              format="DD.MM.YYYY"
            />,
            <Button
              loading={downloading}
              disabled={loading || downloading}
              key="1"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                this.downloadExcel();
              }}
            >
              İndir
            </Button>,
          ]}
        />
        <PageContainer
          style={{ paddingTop: 0 }}
          title=""
          subTitle={`${currentMerch?.number} ${currentMerch?.type}'li terminal bazlı günsonu raporu listelenmektedir.`}
        >
          <Table
            loading={loading}
            dataSource={reports}
            columns={columns}
            rowKey={(record, index) => index}
          />
        </PageContainer>
      </>
    );
  }
}

EodReportByBranch.contextType = AuthContext;

const EodReportByBranchWrapper = (props) => {
  const params = useParams();
  return <EodReportByBranch {...props} params={params} />;
};

EodReportByBranch.propTypes = {
  params: PropTypes.shape({
    branchId: PropTypes.string.isRequired,
    terminalId: PropTypes.string.isRequired,
  }).isRequired,
};

export  default EodReportByBranchWrapper;
